import { useEffect, useRef, useState } from "react";

import InlineImageGallery from "../../../shared/inlineImageGallery/InlineImageGallery";
import SharedMediaViewer from "../../../shared/mediaViewer/SharedMediaViewer";
import "./detailGallery.css";

// DetailGallery — section 2. Canonical detail.html lines 198–412.
//
// Phase coverage of this file:
//   * PRE-ROADMAP-STAGING-QA-CORRECTION-A1 — Splide removed from Property
//     detail. One horizontal non-wrapping rail + prev/next scroll.
//     SharedMediaViewer remains for fullscreen photos/plans.
//   * R3.3C — YouTube iframe lifecycle (plain embed, no SDK)        ✅
//
// External / separate concerns:
//   * R3.3B — Bootstrap modal residue cleanup lives in
//     RealtyDetailScreenContent.js (route-scoped, not gallery-scoped)
//     and remains for the video modal only.
//   * PropertyCard / profile still mount window.Splide via the CDN
//     in public/index.html. This gallery must not use class `splide`
//     without mounting (Splide CSS hides `.splide { visibility: hidden }`).
//
// Image splits: kind='image' → rail items.
//                kind='plan'  → plan-only shared viewer item set.
// Both kinds come from PropertyDetailSerializer.images (single list,
// kind discriminator). Incoming `property.images` order is the #434
// persisted (property, kind) position order — do not re-sort.

const FALLBACK_IMAGE = "/images/no-photo.png";

const PHOTO_VIEWER_TEST_IDS = {
  root: "realty-photo-viewer",
  backdrop: "realty-photo-viewer-backdrop",
  toolbar: "realty-photo-viewer-toolbar",
  canvas: "realty-photo-viewer-canvas",
  loading: "realty-photo-viewer-loading",
  error: "realty-photo-viewer-error",
  image: "realty-photo-viewer-image",
  navPrev: "realty-photo-viewer-nav-prev",
  navNext: "realty-photo-viewer-nav-next",
  preloadPrev: "realty-photo-viewer-preload-prev",
  preloadNext: "realty-photo-viewer-preload-next",
  counter: "realty-photo-viewer-counter",
};

const PLAN_VIEWER_TEST_IDS = {
  root: "realty-plan-viewer",
  backdrop: "realty-plan-viewer-backdrop",
  toolbar: "realty-plan-viewer-toolbar",
  canvas: "realty-plan-viewer-canvas",
  loading: "realty-plan-viewer-loading",
  error: "realty-plan-viewer-error",
  image: "realty-plan-viewer-image",
  navPrev: "realty-plan-viewer-nav-prev",
  navNext: "realty-plan-viewer-nav-next",
  preloadPrev: "realty-plan-viewer-preload-prev",
  preloadNext: "realty-plan-viewer-preload-next",
  counter: "realty-plan-viewer-counter",
};

const extractYoutubeId = (url) => {
  if (typeof url !== "string" || !url) return null;
  const shortMatch = url.match(/youtu\.be\/([\w-]{11})/);
  if (shortMatch) return shortMatch[1];
  const watchMatch = url.match(/[?&]v=([\w-]{11})/);
  if (watchMatch) return watchMatch[1];
  const embedMatch = url.match(/youtube\.com\/embed\/([\w-]{11})/);
  if (embedMatch) return embedMatch[1];
  return null;
};

function toViewerItems(images, kind) {
  return images
    .filter((img) => img && img.image)
    .map((img, index) => ({
      id: img.id,
      src: img.image,
      alt: kind === "plan" ? `Планування ${index + 1}` : `Фото ${index + 1}`,
      downloadUrl: img.image,
      kind,
    }));
}

function DetailGallery({ property }) {
  const allImages = Array.isArray(property.images) ? property.images : [];
  const photoImages = allImages.filter(
    (img) => img && img.kind === "image",
  );
  const planImages = allImages.filter((img) => img && img.kind === "plan");
  const photoItems = toViewerItems(photoImages, "image");
  const planItems = toViewerItems(planImages, "plan");
  const galleryItems = photoImages.length
    ? photoImages.map((img, index) => ({
      id: img.id ?? `photo-${index}`,
      src: img.image || FALLBACK_IMAGE,
      alt: `Фото ${index + 1}`,
      heroLabel: `Відкрити фото ${index + 1}`,
      kind: "image",
    }))
    : [{
      id: "fallback",
      src: FALLBACK_IMAGE,
      alt: "",
      kind: "image",
    }];

  const photoTriggerRef = useRef(null);
  const planTriggerRef = useRef(null);

  const [activePhotoIndex, setActivePhotoIndex] = useState(0);
  const [photoViewerIndex, setPhotoViewerIndex] = useState(null);
  const [planViewerIndex, setPlanViewerIndex] = useState(null);

  const videoModalRef = useRef(null);
  const [videoActive, setVideoActive] = useState(false);
  const videoId = extractYoutubeId(property?.video_url);

  useEffect(() => {
    if (!videoId) return undefined;
    const modalEl = videoModalRef.current;
    if (!modalEl) return undefined;

    const handleShow = () => setVideoActive(true);
    const handleHidden = () => setVideoActive(false);

    modalEl.addEventListener("show.bs.modal", handleShow);
    modalEl.addEventListener("hidden.bs.modal", handleHidden);

    return () => {
      modalEl.removeEventListener("show.bs.modal", handleShow);
      modalEl.removeEventListener("hidden.bs.modal", handleHidden);
    };
  }, [videoId]);

  const openPhotoViewer = (index) => {
    if (!photoItems.length) return;
    const next = Math.min(Math.max(index, 0), photoItems.length - 1);
    setPlanViewerIndex(null);
    setActivePhotoIndex(next);
    setPhotoViewerIndex(next);
  };

  const openPlanViewer = (trigger) => {
    if (!planItems.length) return;
    planTriggerRef.current = trigger || null;
    setPhotoViewerIndex(null);
    setPlanViewerIndex(0);
  };

  const handlePhotoIndexChange = (next) => {
    setPhotoViewerIndex(next);
    setActivePhotoIndex(next);
  };

  return (
    <section className="detail-gallery gallery-section">
      <div className="container">
        <div className="row">
          <div className="gallery row mt-4">
            <section
              id="image-carousel"
              className="detail-gallery__stage"
              aria-label="Фото оголошення"
            >
              <div className="addition-info-images">
                {planImages.length > 0 ? (
                  <button
                    type="button"
                    className="btn btn-primary"
                    data-testid="detail-gallery-open-plans"
                    onClick={(event) => {
                      event.stopPropagation();
                      openPlanViewer(event.currentTarget);
                    }}
                  >
                    <span className="d-none d-md-block">Планування</span>
                    <i
                      className="fa-solid fa-vector-square"
                      aria-hidden="true"
                    />
                  </button>
                ) : null}
                {videoId ? (
                  <button
                    type="button"
                    className="btn btn-primary"
                    data-bs-toggle="modal"
                    data-bs-target="#detailModalVideo"
                  >
                    <span className="d-none d-md-block">Відео</span>
                    <i className="fa-solid fa-video" aria-hidden="true" />
                  </button>
                ) : null}
              </div>
              <div className="detail-gallery__stage-body">
                <InlineImageGallery
                  items={galleryItems}
                  activeIndex={activePhotoIndex}
                  onActiveIndexChange={setActivePhotoIndex}
                  onHeroClick={photoItems.length ? openPhotoViewer : undefined}
                  heroTriggerRef={photoTriggerRef}
                  emptyLabel=""
                  ariaLabel="Галерея фото"
                  testIds={{
                    hero: photoItems.length
                      ? "detail-gallery-hero"
                      : "detail-gallery-photo-0",
                    prev: "detail-gallery-prev",
                    next: "detail-gallery-next",
                    thumb: (index) => `detail-gallery-photo-${index}`,
                  }}
                />
              </div>
            </section>

            <div
              ref={videoModalRef}
              className="modal carousel-modal fade"
              id="detailModalVideo"
              tabIndex={-1}
              role="dialog"
              aria-hidden="true"
            >
              <div className="row">
                <div className="modal-dialog" role="document">
                  <div className="modal-content">
                    <div className="modal-header">
                      <button
                        id="video-close1"
                        type="button"
                        className="close"
                        data-bs-dismiss="modal"
                        aria-label="Close"
                      >
                        <span aria-hidden="true">&times;</span>
                      </button>
                    </div>
                    <div className="modal-body">
                      <div className="video-section">
                        <div id="player">
                          {videoId ? (
                            videoActive ? (
                              <iframe
                                src={`https://www.youtube.com/embed/${videoId}?playsinline=1`}
                                title="Відео оголошення"
                                width="640"
                                height="390"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                              />
                            ) : null
                          ) : (
                            <span className="muted">Відео недоступне</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button
                        id="video-close2"
                        type="button"
                        className="btn-more"
                        data-bs-dismiss="modal"
                      >
                        Закрити
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {photoViewerIndex != null ? (
        <SharedMediaViewer
          items={photoItems}
          activeIndex={photoViewerIndex}
          onIndexChange={handlePhotoIndexChange}
          onClose={() => setPhotoViewerIndex(null)}
          allowDownload
          restoreFocusRef={photoTriggerRef}
          title="Перегляд фото"
          testIds={PHOTO_VIEWER_TEST_IDS}
        />
      ) : null}
      {planViewerIndex != null ? (
        <SharedMediaViewer
          items={planItems}
          activeIndex={planViewerIndex}
          onIndexChange={setPlanViewerIndex}
          onClose={() => setPlanViewerIndex(null)}
          allowDownload
          restoreFocusRef={planTriggerRef}
          title="Перегляд планування"
          testIds={PLAN_VIEWER_TEST_IDS}
        />
      ) : null}
    </section>
  );
}

export default DetailGallery;
